/**
 * SecureRandom.js
 * 
 * Provides cryptographically secure random number generation for the virtual deck system.
 * This is more secure than Math.random() for cryptographic purposes.
 */

/**
 * Generates a cryptographically secure random string
 * @param {number} length - The length of the random string
 * @returns {string} - A secure random string
 */
export const generateSecureRandomString = (length = 32) => {
  const array = new Uint8Array(length);
  window.crypto.getRandomValues(array);
  return Array.from(array, byte => byte.toString(16).padStart(2, '0')).join('');
};

/**
 * Generates a secure random number between 0 and 1
 * @returns {number} - A secure random number between 0 and 1
 */
export const secureRandom = () => {
  const array = new Uint8Array(1);
  window.crypto.getRandomValues(array);
  return array[0] / 255;
};

/**
 * Generates a secure random integer between min and max (inclusive)
 * @param {number} min - The minimum value
 * @param {number} max - The maximum value
 * @returns {number} - A secure random integer
 */
export const secureRandomInt = (min, max) => {
  const range = max - min + 1;
  const bytesNeeded = Math.ceil(Math.log2(range) / 8);
  const maxValue = Math.pow(256, bytesNeeded);
  const array = new Uint8Array(bytesNeeded);
  
  let value;
  do {
    window.crypto.getRandomValues(array);
    value = 0;
    for (let i = 0; i < bytesNeeded; i++) {
      value = (value << 8) + array[i];
    }
  } while (value >= maxValue - (maxValue % range));
  
  return min + (value % range);
};
